/**
 * View-aware task defaults.
 *
 * Determines the scheduling parameters (startAt, startAtType, startAtSubType)
 * and parentPath for a new task based on the current view context.
 *
 * Usage:
 *   const defaults = getTaskDefaults("today")
 *   const id = await addTask(title, defaults.parentPath)
 *   if (defaults.schedule) await scheduleTask(id, ...defaults.schedule)
 */

import type { ViewId } from "@/features/views/viewRegistry"
import { StartAtType, StartAtSubType } from "@/db/schema"
import { toDbDate } from "@/shared/utils/date/dateUtils"
import dayjs from "dayjs"

export interface TaskScheduleDefaults {
  startAt: string
  type: StartAtType
  subType: StartAtSubType
}

export interface TaskCreationDefaults {
  /** Parent path for the new task */
  parentPath: string
  /** Schedule parameters — undefined means no scheduling (anyday/inbox) */
  schedule?: TaskScheduleDefaults
}

/**
 * Compute sensible defaults for a new task in a given view.
 *
 * @param viewId - The current view
 * @param containerId - Optional container ID (for project/list views)
 */
export function getTaskDefaults(
  viewId: ViewId,
  containerId?: string,
  eveningHour = 18,
): TaskCreationDefaults {
  // Dynamic container views — inherit parentPath
  if (
    (viewId === "project" || viewId === "list" || viewId === "area") &&
    containerId
  ) {
    return { parentPath: `root/${containerId}` }
  }

  // Static views with scheduling
  switch (viewId) {
    case "today":
      return {
        parentPath: "root/#anyday",
        schedule: {
          startAt: toDbDate(dayjs()),
          type: StartAtType.Default,
          subType: StartAtSubType.Default,
        },
      }

    case "evening": {
      // If it's already past evening time, schedule for tomorrow evening
      const date =
        dayjs().hour() >= eveningHour ? dayjs().add(1, "day") : dayjs()
      return {
        parentPath: "root/#anyday",
        schedule: {
          startAt: toDbDate(date),
          type: StartAtType.Default,
          subType: StartAtSubType.ThisEvening,
        },
      }
    }

    case "someday":
      return {
        parentPath: "root/#anyday",
        schedule: {
          startAt: toDbDate(dayjs()),
          type: StartAtType.Someday,
          subType: StartAtSubType.Default,
        },
      }

    case "upcoming":
      return {
        parentPath: "root/#anyday",
        schedule: {
          startAt: toDbDate(dayjs().add(1, "day")),
          type: StartAtType.Default,
          subType: StartAtSubType.Default,
        },
      }

    case "inbox":
      return { parentPath: "root/#inbox" }

    case "anyday":
      return { parentPath: "root/#anyday" }

    // Search, logbook, trash — default to inbox
    default:
      return { parentPath: "root/#inbox" }
  }
}
